import type {
  MonacoTypeScriptCompanionModel,
  MonacoTypeScriptExtraLib,
} from "@src/components/CodeEditors/MonacoTypeScriptEditor";

import type { WorkspaceDependency } from "../types";

/**
 * Domain-free Monaco model plumbing for a code workspace: how editable files map
 * to model URIs, how declared npm dependencies become ambient module shims, and
 * how the hidden companion models (locked contract module, contract-check,
 * per-domain extras) are assembled for the active tab.
 *
 * Everything domain-specific — the URIs, the `.d.ts` text, the contract-check
 * source and its user-visible diagnostic — arrives as {@link WorkspaceMonacoConfig}.
 */

/** The Monaco model URI for an editor file by its workspace filename. */
export const workspaceModelUri = (filename: string): string =>
  `file:///${filename}`;

/**
 * Ambient `declare module` shims so a bare `import x from "pkg"` type-checks
 * against the declared dependency set without fetching real types. The `"*"`
 * wildcard keeps unlisted imports from erroring in the editor — the publish
 * `deps_locked` gate is what actually rejects them.
 */
const dependencyAmbientTypes = (
  dependencies: WorkspaceDependency[]
): string => {
  const declared = new Set<string>(["*"]);
  for (const dependency of dependencies) {
    declared.add(dependency.name);
    declared.add(`${dependency.name}/*`);
  }

  return [...declared]
    .sort()
    .map(
      (name) =>
        `declare module "${name}" { const value: any; export default value; }`
    )
    .join("\n");
};

/** A hidden companion asserting the entry module satisfies the domain contract. */
export interface WorkspaceContractCheck {
  uri: string;
  source: string;
  /**
   * The only text the user ever sees for a contract-check failure —
   * `sanitizeMarkers` collapses every companion error into this one marker so
   * internal `file:///` URIs never leak. Name the export and the type in it.
   */
  diagnosticMessage: string;
}

/** The locked, environment-injected contract module shown as a read-only tab. */
export interface WorkspaceTypesModule {
  filename: string;
  modelUri: string;
  source: string;
}

/** Everything the editor needs to build models for one domain. */
export interface WorkspaceMonacoConfig {
  /** URI for the ambient logger `.d.ts` (no relative imports → `node_modules` is fine). */
  loggerLibUri: string;
  /** Ambient logger declarations, resolved without an import in every file. */
  loggerDts: string;
  /** URI for the generated dependency shims. */
  dependencyLibUri: string;
  /** The locked contract-types module. */
  typesModule: WorkspaceTypesModule;
  /** `null` for domains with no static entry-shape assertion. */
  contractCheck: WorkspaceContractCheck | null;
  /**
   * Additional locked, environment-injected modules shown as read-only tabs
   * after {@link typesModule} — e.g. Agentic Logic's `sdk.d.ts`. Each is
   * registered as a real ESM companion model at its own URI, exactly like
   * `typesModule`, so relative imports between locked tabs resolve.
   */
  readonlyTabs?: readonly WorkspaceTypesModule[];
  /**
   * Additional always-registered HIDDEN companions — never shown as a tab.
   * Distinct from {@link readonlyTabs}, which is the same wiring but visible.
   */
  extraCompanions?: MonacoTypeScriptCompanionModel[];
  /**
   * Hidden modules only a TEST file imports — Agentic Logic's `vitest` surface
   * and its `./test-harness`. Registered only while a test file is open and
   * disposed when its editor unmounts (Monaco models are global, so a
   * registration alone would outlive the tab), so rule source can never
   * type-check against a test-only import. Empty for a domain whose suites
   * are not editor files.
   */
  testSuiteCompanions: readonly MonacoTypeScriptCompanionModel[];
}

/**
 * The models whose markers make up the RULE's verdict: every editable file,
 * the locked contract modules, the domain's hidden extras and the contract
 * check — exactly what the active model's verdict covers on a rule tab, and
 * never a test file or the test-only modules. While a test file is open this
 * is what the entry companion's verdict is scoped to, so Save and Publish gate
 * on the same judgement the rule tab would give.
 */
export const buildWorkspaceRuleVerdictUris = (
  config: WorkspaceMonacoConfig,
  files: Record<string, string>
): string[] => [
  ...Object.keys(files).map(workspaceModelUri),
  config.typesModule.modelUri,
  ...(config.readonlyTabs ?? []).map((tab) => tab.modelUri),
  ...(config.extraCompanions ?? []).map((model) => model.uri),
  ...(config.contractCheck ? [config.contractCheck.uri] : []),
];

/** Ambient libs registered alongside every model. */
export const buildWorkspaceExtraLibs = (
  config: WorkspaceMonacoConfig,
  dependencies: WorkspaceDependency[]
): MonacoTypeScriptExtraLib[] => [
  {
    // `logger` resolves ambiently in every editor file — no import is ever
    // shown or written; the real import is auto-injected at bundle/test time.
    uri: config.loggerLibUri,
    content: config.loggerDts,
  },
  {
    uri: config.dependencyLibUri,
    content: dependencyAmbientTypes(dependencies),
  },
];

/**
 * Companion models registered alongside the active editor model so cross-file
 * imports type-check: every editable file OTHER than the active one (the active
 * file's model is owned by the editor via its `path`), the readonly contract
 * module and any other locked read-only tabs (unless one of them is itself the
 * active tab), any domain extras, the hidden contract-check, and — only while
 * a test file is open — the test-only modules.
 */
export const buildWorkspaceCompanionModels = (
  config: WorkspaceMonacoConfig,
  files: Record<string, string>,
  activeFile: string,
  isTestFileActive: boolean
): MonacoTypeScriptCompanionModel[] => {
  const companions: MonacoTypeScriptCompanionModel[] = [];

  for (const [filename, content] of Object.entries(files)) {
    if (filename === activeFile) {
      continue;
    }
    companions.push({ uri: workspaceModelUri(filename), content });
  }

  if (activeFile !== config.typesModule.filename) {
    companions.push({
      // Real ESM module so `import ... from "./types"` type-checks. Shown
      // read-only as a locked tab in the editor.
      uri: config.typesModule.modelUri,
      content: config.typesModule.source,
    });
  }

  for (const tab of config.readonlyTabs ?? []) {
    // When one of these tabs is itself open, its own model is the primary
    // model (owned by the editor via `path`) — registering it again here
    // would create a second model at the same URI.
    if (activeFile === tab.filename) {
      continue;
    }
    companions.push({ uri: tab.modelUri, content: tab.source });
  }

  if (config.extraCompanions) {
    companions.push(...config.extraCompanions);
  }

  if (isTestFileActive) {
    companions.push(
      ...config.testSuiteCompanions.map((model) => ({
        ...model,
        disposeOnUnmount: true,
      }))
    );
  }

  if (config.contractCheck) {
    companions.push({
      uri: config.contractCheck.uri,
      content: config.contractCheck.source,
      diagnosticMessage: config.contractCheck.diagnosticMessage,
    });
  }

  return companions;
};
